import React, { useState } from 'react';
import axios from 'axios';
import { io } from 'socket.io-client';
import exportToWord from '../../utils/DocumentExporter';
import { toast } from 'react-toastify';
import { FaFileWord, FaGoogleDrive, FaChevronDown, FaDownload, FaSpinner } from 'react-icons/fa';
import PricingModalInsufficientCredits from '../shared/PricingModalInsufficientCredits';

const SOCKET_URL = process.env.REACT_APP_SOCKET_URL || 'https://d2on4527nsm8b5.cloudfront.net';
const API_URL = process.env.REACT_APP_API_URL || 'https://d2bwumaosaqsqc.cloudfront.net';

export default function ExportPresentationButton({ documentID, pptName, outlines }: { documentID?: string | null; pptName?: string | null; outlines?: any[] }) {
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showInsufficientModal, setShowInsufficientModal] = useState(false);


  // Always open modal, regardless of credits
  const handleExport = async () => {
    setShowInsufficientModal(true);
  };

  const handleExportToWord = async () => {
    try {
      setLoading(true);
      setIsDropdownOpen(false);
      toast.info('Preparing document for download...');

      const authToken = sessionStorage.getItem('authToken');
      const orgID = sessionStorage.getItem('orgId') || '';

      // Check credits before exporting (20 credits per export)
      try {
        if (authToken && orgID) {
          const profileRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgID}`, { headers: { Authorization: `Bearer ${authToken}` } });
          const creditsAvailable = profileRes.data?.credits ?? 0;
          const planName = profileRes.data?.plan?.plan_name || 'free';
          if (planName === 'free' && creditsAvailable < 20) {
            // show an upsell modal instead of only a toast
            setShowInsufficientModal(true);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.warn('Failed to fetch credits before export, proceeding cautiously', err);
      }

      // Ensure we have outlines; if documentID present but outlines empty, fetch outlines
      let usedOutlines = outlines || [];
      if (documentID && (!usedOutlines || usedOutlines.length === 0)) {
        const res = await axios.get(`${API_URL}/api/v1/outline/${documentID}/documentGenerationOutline`, { headers: { Authorization: `Bearer ${authToken}` } });
        usedOutlines = res.data?.outline || [];
      }

      // Connect socket and reuse it while fetching all outlines in parallel.
      const socket = io(`${SOCKET_URL}/generated-data`, { auth: authToken ? { token: authToken } : undefined });

      // Wait for connect (short timeout)
      await new Promise<void>((resolve) => {
        const t = setTimeout(() => resolve(), 2000);
        socket.on('connect', () => { clearTimeout(t); resolve(); });
      });

      const dataMap: Record<string, any[]> = {};

      // Helper to normalize an item's outline id
      const normalizeOutlineId = (item: any) => {
        return item?.outlineID || item?.outlineId || item?.outline || item?.sectionId || item?.sectionID || item?.section || null;
      };

      // Fallback collector in case server only emits a bulk 'generatedData' event.
      const fallbackData: any[] = [];
      const onBulk = (d: any[]) => {
        if (!Array.isArray(d)) return;
        d.forEach(it => {
          try { it.__normalizedOutline = normalizeOutlineId(it); } catch (e) { it.__normalizedOutline = null; }
          fallbackData.push(it);
        });
      };
      socket.on('generatedData', onBulk);

      // Perform all outline fetches in parallel with Promise.all and modest per-outline timeouts.
      await Promise.all(usedOutlines.map(async (outline) => {
        const oid = outline.outlineID || outline.outlineId || outline.id || outline._id || String(outline.title || outline.outlineID);
        // Create a promise that resolves when either the per-outline event arrives or when timeout hits and we fallback to collected data
        dataMap[oid] = await new Promise<any[]>((resolve) => {
          let settled = false;
          const timeoutMs = 2500; // smaller timeout to avoid long sequential waits

          const timer = setTimeout(() => {
            if (settled) return;
            settled = true;
            // try matching from fallbackData
            const matched = fallbackData.filter(it => String(it.__normalizedOutline || normalizeOutlineId(it)) === String(oid));
            resolve(matched);
          }, timeoutMs);

          const eventName = `generatedData:${oid}`;
          const listener = (d: any[]) => {
            if (settled) return;
            settled = true;
            clearTimeout(timer);
            socket.off(eventName, listener);
            const items = Array.isArray(d) ? d.map(it => ({ ...it, __normalizedOutline: normalizeOutlineId(it) })) : [];
            resolve(items);
          };

          socket.on(eventName, listener);
          try { socket.emit('join-outline', oid); } catch (e) { /* ignore */ }
          socket.emit('fetchGeneratedData', { outlineID: oid, authToken });
        });
      }));

      // cleanup listeners and disconnect
      socket.off('generatedData', onBulk);
      socket.disconnect();

  // debug: print counts per-outline we collected
  console.log('ExportPresentationButton: About to export. Outlines:', usedOutlines.map(o => o.id || o._id || o.outlineID));
  console.log('ExportPresentationButton: Collected data counts:', Object.fromEntries(Object.entries(dataMap).map(([k,v]) => [k, Array.isArray(v) ? v.length : 0])));
  
  // Call exporter
  const success = await exportToWord(usedOutlines, dataMap, pptName || 'Presentation');
  
  if (success) {
    toast.success('Download started');
    // Deduct credits on successful export for free plan
    try {
      const orgID = sessionStorage.getItem('orgId') || '';
      const authToken = sessionStorage.getItem('authToken');
      if (authToken && orgID) {
        const profileRes = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organization/${orgID}`, { headers: { Authorization: `Bearer ${authToken}` } });
        const creditsAvailable = profileRes.data?.credits ?? 0;
        const planName = profileRes.data?.plan?.plan_name || 'free';
        if (planName === 'free') {
          const newCredits = Math.max(0, creditsAvailable - 20);
          // Patch new credits
          await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/v1/data/organizationprofile/organizationedit/${orgID}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
            body: JSON.stringify({ credits: newCredits }),
          });
          toast.info(`20 credits deducted. Credits left: ${newCredits}`);
        }
      }
    } catch (err) {
      console.warn('Failed to deduct credits after export', err);
    }
  } else {
    toast.error('Export failed');
  }
    } catch (err) {
      console.error('ExportPresentationButton error', err);
      toast.error('Export failed. See console for details.');
    } finally {
      setLoading(false);
    }
  };

  const handleExportToGoogleDocs = async () => {
    setIsDropdownOpen(false);
    toast.info('Google Docs export will be available soon!', {
      position: 'top-right',
      autoClose: 3000
    });
  };

  const closeInsufficientModal = () => setShowInsufficientModal(false);

  return (
    <div className="relative">
      {/* Main Export Button */}
      <div className="flex">
        <button
          onClick={handleExport}
          disabled={loading}
          className="flex items-center gap-1 px-3 py-2 lg:py-2 lg:h-[2.5rem] bg-[#3667B2] border border-[#3667B2] text-white rounded-l text-base font-medium hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <FaSpinner className="animate-spin" />
              <span>Preparing...</span>
            </>
          ) : (
            <>
              <FaDownload />
              <span className="hidden sm:inline">Export</span>
            </>
          )}
        </button>
        
        {/* Dropdown Toggle */}
        <button
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          disabled={loading}
          className="px-2 py-2 lg:py-2 lg:h-[2.5rem] bg-[#3667B2] border border-[#3667B2] text-white rounded-r text-base border-l-0 hover:bg-blue-600 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <FaChevronDown className={`transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Dropdown Menu */}
      {isDropdownOpen && (
        <div className="absolute top-full left-0 mt-2 w-56 bg-white rounded-lg shadow-xl border border-gray-200 z-[9999] overflow-hidden">
          <div className="py-2">
            <button
              onClick={handleExportToWord}
              disabled={loading}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
                <FaFileWord className="text-blue-600 text-lg" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Export as Word</div>
                <div className="text-sm text-gray-500">Download as DOCX file</div>
              </div>
            </button>
            
            <button
              onClick={handleExportToGoogleDocs}
              disabled={loading}
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center justify-center w-8 h-8 bg-blue-100 rounded-lg">
                <FaGoogleDrive className="text-blue-600 text-lg" />
              </div>
              <div>
                <div className="font-medium text-gray-900">Export to Google Docs</div>
                <div className="text-sm text-gray-500">Save to Google Drive (Coming Soon)</div>
              </div>
            </button>
          </div>
        </div>
      )}
      
      {/* Backdrop to close dropdown */}
      {isDropdownOpen && (
        <div 
          className="fixed inset-0 z-[9998]" 
          onClick={() => setIsDropdownOpen(false)}
        />
      )}

      {/* Insufficient credits modal (if needed) */}
      {showInsufficientModal && (
        <PricingModalInsufficientCredits
          onClose={closeInsufficientModal}
          authToken={sessionStorage.getItem('authToken') || undefined}
          orgId={sessionStorage.getItem('orgId') || undefined}
          exportHandler={async () => {
            setShowInsufficientModal(false);
            await handleExportToWord();
          }}
        />
      )}
    </div>
  );
}
